/**
 * Central store for every settings page's values. Pages themselves only
 * declare fields and defaults (see ./types, ./registry) — this is the "main
 * setting mechanism" they're linked to: it persists edits to localStorage
 * and is the single place non-UI code (autosave.ts, documentStore.ts, ...)
 * reads a setting's current value from.
 *
 * Values live in one of two places depending on the item's `encrypted` flag:
 *
 *  - plain items → `values`, persisted as JSON in localStorage, readable
 *    before any project is unlocked (the language, the plugin URL, which
 *    cloud backend to use — all needed to get as far as the unlock screen).
 *  - `encrypted` items → `secrets`, held in memory only and persisted as a
 *    single AES-GCM blob keyed to the active project. settingsSecrets.ts owns
 *    that lifecycle; until it unlocks them, reads fall back to the item's
 *    default and writes are refused.
 */

import { create } from "zustand";
import { settingsPages } from "./registry";
import type { SettingItem, SettingValue } from "./types";
import cloudEvents from "../cloud/events";

const STORAGE_KEY = "yaqadocs.settings.v1";

type SettingsValues = Record<string, Record<string, SettingValue>>;

function loadStoredValues(): SettingsValues {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function itemFor(pageId: string, key: string): SettingItem | undefined {
  return settingsPages.find((p) => p.id === pageId)?.items.find((i) => i.key === key);
}

function defaultFor(pageId: string, key: string): SettingValue | undefined {
  return itemFor(pageId, key)?.default;
}

/** True when this item's value belongs in the encrypted blob, not localStorage. */
export function isEncryptedSetting(pageId: string, key: string): boolean {
  return itemFor(pageId, key)?.encrypted === true;
}

/** Every declared `encrypted` item, as [pageId, key] pairs. */
export function encryptedSettingKeys(): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  for (const page of settingsPages) {
    for (const item of page.items) {
      if (item.encrypted) pairs.push([page.id, item.key]);
    }
  }
  return pairs;
}

function persist(values: SettingsValues): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // localStorage unavailable (private mode / quota) — in-memory only for this session.
  }
}

/** Thrown when an encrypted setting is written while no project is unlocked. */
export class SettingsLockedError extends Error {
  constructor() {
    super("Open a project first — this setting is stored encrypted with the project key.");
    this.name = "SettingsLockedError";
  }
}

interface SettingsStore {
  values: SettingsValues;
  /** Decrypted `encrypted` items. Empty and unreadable until unlocked. */
  secrets: SettingsValues;
  /** True once settingsSecrets.ts has decrypted the blob for the open project. */
  secretsUnlocked: boolean;

  getValue: (pageId: string, key: string) => SettingValue | undefined;
  setValue: (pageId: string, key: string, value: SettingValue) => void;
  /** settingsSecrets.ts only — installs the decrypted secrets. */
  setSecrets: (secrets: SettingsValues, unlocked: boolean) => void;
  /** settingsSecrets.ts only — replaces plaintext values wholesale after a migration. */
  replaceValues: (values: SettingsValues) => void;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  values: loadStoredValues(),
  secrets: {},
  secretsUnlocked: false,

  getValue: (pageId, key) => {
    if (isEncryptedSetting(pageId, key)) {
      // While locked there is deliberately nothing to read — callers get the
      // item's default, which for a credential means "not configured".
      const stored = get().secrets[pageId]?.[key];
      return stored !== undefined ? stored : defaultFor(pageId, key);
    }
    const stored = get().values[pageId]?.[key];
    return stored !== undefined ? stored : defaultFor(pageId, key);
  },

  setValue: (pageId, key, value) => {
    if (isEncryptedSetting(pageId, key)) {
      if (!get().secretsUnlocked) throw new SettingsLockedError();
      set((state) => ({
        secrets: { ...state.secrets, [pageId]: { ...state.secrets[pageId], [key]: value } },
      }));
      // Written asynchronously (it has to derive nothing but does have to
      // encrypt); a failure surfaces through the settings field's own error
      // path rather than silently dropping the edit.
      void persistSecrets();
      cloudEvents.emit("settingsUpdated");
      return;
    }

    set((state) => {
      const nextValues: SettingsValues = {
        ...state.values,
        [pageId]: { ...state.values[pageId], [key]: value },
      };
      persist(nextValues);
      return { values: nextValues };
    });
    // src/cloud/sync.ts and src/cloud/s3.ts listen for this to re-read the
    // active provider / invalidate cached credentials after a settings edit.
    cloudEvents.emit("settingsUpdated");
  },

  setSecrets: (secrets, unlocked) => set({ secrets, secretsUnlocked: unlocked }),

  replaceValues: (values) => {
    persist(values);
    set({ values });
  },
}));

/** Non-reactive read, for plain modules (autosave.ts, documentStore.ts). */
export function getSettingValue(pageId: string, key: string): SettingValue | undefined {
  return useSettingsStore.getState().getValue(pageId, key);
}

/**
 * Set by settingsSecrets.ts at boot. Kept as an injected callback rather than
 * a direct import so this module stays free of the crypto and project-store
 * dependencies — settings/registry.ts is already webpack-only (require.context),
 * and pulling more into that graph makes it harder to test.
 */
let persistSecrets: () => Promise<void> = async () => {};

export function setSecretsPersister(fn: () => Promise<void>): void {
  persistSecrets = fn;
}
